import {
  riskmedicalLoadBegin,
  riskmedicalLoadSuccess,
  riskmedicalLoadFailed,
  riskmedicalSaveBegin,
  riskmedicalSaveSuccess,
  riskmedicalSaveFailed,
  riskmedicalSelectDefaultLastAcceptedCover,
  riskmedicalBenefitPlanFilter,
  riskmedicalBenefitPlan,
  riskmedicalEnrolmentDone,
  riskmedicalCleanState,
  riskmedicalPerMemberSelection,
} from "../actions/riskmedicalActions";
import { linkLogicRelationships, toCamelImproved } from "cxa-utils";
import lodash from "lodash";
import { camelize } from "humps";
import { riskmedicalPerMemberUtils, riskmedicalDependancyOptionUtils } from "./utils";

import accounting from "accounting";

const makeBenefitKeysMap = data => {
  const mapping = {};

  const panelData = lodash.get(data, "enrolmentDetails.panel.data");
  const benefits = panelData.find(element => element.id === "Benefits");
  const records = lodash.get(benefits, "dataListing.record");

  if (Array.isArray(records)) {
    records.forEach(record => {
      const name = record.data.find(element => element.id === "BenefitName").value;

      mapping[record.benefitId] = name;
    });
  } else {
    const name = records.data.find(element => element.id === "BenefitName").value;

    mapping[records.benefitId] = name;
  }

  return mapping;
};

const prepareDataToSave = benefits => {
  const dataToSave = [];

  const getDependantKeys = (option, eligibleMembersData, benefit) => {
    const dependants = [];

    // make sure that eligibleMembers data is an array. needed to implement to handle EF only plan option (APL-3294)
    let eligibleMembers = [];

    if (!Array.isArray(eligibleMembersData) && !eligibleMembersData.members) {
      eligibleMembers = [].concat(eligibleMembersData);
    } else if (eligibleMembersData.members && Array.isArray(eligibleMembersData.members)) {
      // For per member selection
      eligibleMembers = eligibleMembersData.members;
    } else {
      eligibleMembers = [...eligibleMembersData]; // clone the array instead of using reference
    }
    const selectionMode = eligibleMembers[0].selectionMode
      ? eligibleMembers[0].selectionMode.toLowerCase()
      : "";

    if (benefit && benefit.filters) {
      const isNone =
        benefit.currentGroupName === "No Coverage" || benefit.currentGroupName === "None";

      eligibleMembers.forEach(eligibleMember => {
        if (
          eligibleMember.type !== "Employee" &&
          option &&
          option.type !== "EE" &&
          option.isAvailable &&
          selectionMode !== "individual"
        ) {
          if (
            eligibleMember.type === "Children" ||
            eligibleMember.type === "Spouse" ||
            eligibleMember.type === "Family" ||
            eligibleMember.type === "Parent" ||
            eligibleMember.type === "Parent-in law" ||
            eligibleMember.type === "Sibling"
          ) {
            const memberKey = parseInt(eligibleMember.key);

            if (
              !isNone &&
              memberKey &&
              dependants.indexOf(memberKey) === -1 &&
              eligibleMember.memberType.includes(option.type)
            ) {
              dependants.push(memberKey);
            }
          }
        } else if (
          eligibleMember.type !== "Employee" &&
          selectionMode === "individual" &&
          eligibleMember.isSelected &&
          (eligibleMember.selectable === "true" || eligibleMember.selectable === true)
        ) {
          // for per member selection
          dependants.push(eligibleMember.key);
        }
        return dependants;
      });
    } else if (benefit) {
      eligibleMembers.forEach(eligibleMember => {
        if (
          eligibleMember.type !== "Employee" &&
          eligibleMember.isSelected &&
          (eligibleMember.selectable === "true" || eligibleMember.selectable === true)
        ) {
          // for per member selection
          dependants.push(eligibleMember.key);
        }
      });
    }

    return dependants;
  };

  const getSelectedDependantPlanId = (option, selectionMode = "") => {
    if (
      option.isSelected &&
      (option.isAvailable || selectionMode === "individual") &&
      option.type !== "EE"
    ) {
      return option.planId;
    }

    return null;
  };

  if (Array.isArray(benefits)) {
    benefits.forEach(benefit => {
      const plan = benefit.plans.find(
        plan => plan.isSelected === true && plan.isAvailable === true
      );

      let dependants = [];

      if (plan) {
        let includesEmployee = false;
        let selectedPlanId;
        let tempEligibleMembers = [];
        if (!Array.isArray(plan.eligibleMembers) && !plan.eligibleMembers.members) {
          tempEligibleMembers = [].concat(plan.eligibleMembers);
        } else if (plan.eligibleMembers.members && Array.isArray(plan.eligibleMembers.members)) {
          // For per member selection
          tempEligibleMembers = plan.eligibleMembers.members;
        } else {
          tempEligibleMembers = [...plan.eligibleMembers]; // clone the array instead of using reference
        }
        const benefitSelectionMode = tempEligibleMembers[0].selectionMode
          ? tempEligibleMembers[0].selectionMode.toLowerCase()
          : "";
        if (plan.options && plan.options.length > 0) {
          for (const option of plan.options) {
            if (option.isSelected && plan.isAvailable) {
              includesEmployee = option.type.indexOf("E") >= 0;
              const latestSelectedPlanId = getSelectedDependantPlanId(option, benefitSelectionMode);
              selectedPlanId = latestSelectedPlanId ? latestSelectedPlanId : selectedPlanId;
            }
          }
        }
        if (plan.options && plan.options.length > 0 && benefitSelectionMode !== "individual") {
          for (const option of plan.options) {
            if (option.isSelected && plan.isAvailable && option.isAvailable) {
              dependants = getDependantKeys(option, plan.eligibleMembers, benefit);
            }
          }
        } else {
          includesEmployee = true;
          dependants = getDependantKeys(null, plan.eligibleMembers, benefit);
        }

        // If no plan Id selected, revert to EE plan Id
        if (!selectedPlanId) {
          selectedPlanId = plan.planId ? plan.planId : benefit.id;
        }

        dataToSave.push({
          Benefit: benefit.benefitId,
          Selection: selectedPlanId,
          IsSumAssured: benefit.isSumAssured,
          SumAssured: benefit.isSumAssured ? parseFloat(plan.sumAssured) : 0,
          HasEmployee: includesEmployee,
          Dependants: dependants,
        });
      }
    });
  }

  return dataToSave;
};

const checkIfPlanIsSelected = plan => plan.isSelected;

const checkIfPlanIsAvailable = plan => {
  const member =
    plan.eligibleMembers && plan.eligibleMembers.length
      ? plan.eligibleMembers[0]
      : plan.eligibleMembers.members && plan.eligibleMembers.members.length
      ? plan.eligibleMembers.members[0]
      : null;
  const selectionMode = member ? member.selectionMode : "";
  if (plan.options && Array.isArray(plan.options)) {
    const availableOptions = plan.options.filter(
      option => selectionMode === "Individual" || option.isAvailable === true
    );

    plan.isAvailable = availableOptions && availableOptions.length > 0;
  }
  return plan.isAvailable;
};

const checkIfPlanIsAvailableByValue = (plan, planValue) => {
  const member =
    plan.eligibleMembers && plan.eligibleMembers.length
      ? plan.eligibleMembers[0]
      : plan.eligibleMembers.members && plan.eligibleMembers.members.length
      ? plan.eligibleMembers.members[0]
      : null;
  const selectionMode = member ? member.selectionMode : "";
  if (plan.options && Array.isArray(plan.options)) {
    const selectedOptions = plan.options.filter(
      option =>
        (selectionMode === "Individual" || option.isAvailable === true) &&
        option.planValue === planValue
    );

    return selectedOptions && selectedOptions.length > 0;
  }
  return plan.isAvailable && plan.planValue === planValue;
};

const getSelectedPlanValue = plan => {
  const member =
    plan.eligibleMembers && plan.eligibleMembers.length
      ? plan.eligibleMembers[0]
      : plan.eligibleMembers.members && plan.eligibleMembers.members.length
      ? plan.eligibleMembers.members[0]
      : null;
  const selectionMode = member ? member.selectionMode : "";
  if (plan.options && Array.isArray(plan.options) && selectionMode !== "Individual") {
    const selectedOptions = plan.options.filter(
      option => option.isAvailable && option.isSelected === true
    );

    return selectedOptions && selectedOptions.length > 0 ? selectedOptions[0].planValue : "";
  }

  return plan.planValue;
};

const getSelectedSumAssured = ({ uniqueId, intervalOptions, sumAssured }) => {
  if (intervalOptions && Array.isArray(intervalOptions) && intervalOptions.length > 0) {
    const selectedIntervalOptions = intervalOptions.filter(
      option => option.isAvailable && option.uniqueId === uniqueId
    );

    return selectedIntervalOptions.length > 0 ? selectedIntervalOptions[0].sumAssured : "";
  }

  return sumAssured;
};

const resetPlanSelection = (plan, options) => {
  plan.isSelected = false;
  const flatOptions = [];

  if (options) {
    options.forEach(option => {
      flatOptions[option.type] = option.isSelected;
    });
  }

  if (plan.options) {
    const newOptions = plan.options.map(a => lodash.cloneDeep(a));
    newOptions.forEach(option => {
      option.isSelected = options && flatOptions[option.type] ? flatOptions[option.type] : false;
    });

    plan.options = newOptions;
  }

  plan.isSelected = false;
};

const resetBenefitSelection = benefit => {
  if (benefit && Array.isArray(benefit.plans)) {
    benefit.plans.forEach(plan => {
      // Update deselected plan:
      resetPlanSelection(plan);
    });
  }
};

const isNoCoveragePlan = plan => plan.groupValue === "0" && plan.planValue === "0";

const updateBenefitFilter = (benefit, plan, setAsCurrent) => {
  if (setAsCurrent) {
    benefit.currentGroupName = plan.groupName;
  }

  if (
    benefit.filtersIncludeNone &&
    benefit.filtersIncludeNone.length > 1 &&
    !isNoCoveragePlan(plan)
  ) {
    // we need to update second hardcoded filter (not No Coverage)
    benefit.filtersIncludeNone[1].planGroupName = plan.groupName;
  }
};

const updateIntervalBenefits = plan => {
  const {
    selectedPlan: { planId },
    selectedBenefit: { plans = [] },
  } = plan || {};

  plans.forEach(plan => {
    plan.isSelected = plan.planId === planId;
  });
};

// Update plan selection with dependency options
/*eslint-disable complexity*/
const updateDependencyOptions = (data, updatedLinkedBenefits = []) => {
  const { parentBenefitId, childBenefitId, selectedPlan, benefits } = data;
  if (!selectedPlan || !selectedPlan.dependancyOptions) {
    return;
  }
  let targetBenefitId = parentBenefitId ? parentBenefitId : childBenefitId;
  let targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);

  if (!targetBenefit) {
    targetBenefit = benefits.find(
      benefit => benefit.benefitId === selectedPlan.dependancyOptions.benefitID
    );
    targetBenefitId = targetBenefit.benefitId;
  }

  if (!targetBenefit) {
    return;
  }
  // For benefits that are linked to multiple products, the list
  // of available plans should be based on the filtered list
  // identified in the already processed products
  // e.g. GTLEE_GTLSP and GTLEE_GCIEE
  const isFirstTimeTargetBenefit = updatedLinkedBenefits.indexOf(targetBenefit.benefitId) < 0;
  const currentlyAvailablePlans = [];
  if (!isFirstTimeTargetBenefit) {
    targetBenefit.plans.map(plan => {
      if (plan.isAvailable) {
        currentlyAvailablePlans[plan.planId] = true;
      }
    });
  }

  const dependancyOptions = riskmedicalDependancyOptionUtils.findValidDependancyOptions(
    selectedPlan,
    targetBenefitId
  );
  // For single plan based
  if (!selectedPlan.options) {
    if (targetBenefit && Array.isArray(dependancyOptions) && Array.isArray(targetBenefit.plans)) {
      const optionsMap = {};

      const availableIntervalOptionsMap = {};

      for (const option of dependancyOptions) {
        optionsMap[option.optionID] = {
          isAvailable: !isFirstTimeTargetBenefit
            ? currentlyAvailablePlans[option.optionID] &&
              option.notSelectableDueToBenefitDependancy === "false"
            : option.notSelectableDueToBenefitDependancy === "false",
          message: option.message,
        };

        // Check optionID contains i and numeric for interval options
        if (
          option.optionID.match(/([i])\d/g) &&
          option.notSelectableDueToBenefitDependancy === "false"
        ) {
          availableIntervalOptionsMap[option.optionID] = optionsMap[option.optionID];
        }
      }

      // Check availability for each plan
      targetBenefit.plans = targetBenefit.plans.map(plan => {
        let option = optionsMap[plan.planId] || availableIntervalOptionsMap[plan.planId];

        // Check for available interval options
        let intervalOptions = !isFirstTimeTargetBenefit ? plan.intervalOptions : [];

        if (plan.allIntervalOptions) {
          intervalOptions =
            !isFirstTimeTargetBenefit && intervalOptions
              ? intervalOptions.filter(intervalOpt => {
                  if (availableIntervalOptionsMap[intervalOpt.optionId]) {
                    return intervalOpt;
                  }
                })
              : plan.allIntervalOptions.filter(intervalOpt => {
                  if (availableIntervalOptionsMap[intervalOpt.optionId]) {
                    return intervalOpt;
                  }
                });

          if (intervalOptions && intervalOptions.length > 0) {
            // Check for previously selected plan
            const prevPlanSelected = intervalOptions.find(opt => opt.optionId === plan.planId);

            plan.planValue = prevPlanSelected
              ? prevPlanSelected.planValue
              : intervalOptions[0].planValue;
            plan.groupValue = prevPlanSelected
              ? prevPlanSelected.optionId
              : intervalOptions[0].optionId;
            plan.planId = prevPlanSelected
              ? prevPlanSelected.optionId
              : intervalOptions[0].optionId;
            plan.sumAssured = prevPlanSelected
              ? prevPlanSelected.sumAssured
              : intervalOptions[0].sumAssured;
            plan.uniqueId = prevPlanSelected
              ? prevPlanSelected.uniqueId
              : `${targetBenefit.benefitId}-${intervalOptions[0].optionId}`;
            plan.isHDFRequired = prevPlanSelected
              ? prevPlanSelected.isHDFRequired
              : intervalOptions[0].isHDFRequired;

            plan.isAvailable = prevPlanSelected
              ? prevPlanSelected.isAvailable
              : intervalOptions[0].isAvailable;

            const selectedMembers =
              plan.eligibleMembers && plan.eligibleMembers.members
                ? plan.eligibleMembers.members.filter(
                    member => member.isSelected && member.type !== "Employee"
                  )
                : [];
            const selectedIntervalOption = intervalOptions.find(
              option => option.optionId === plan.planId
            );
            const memberPrice = selectedIntervalOption ? selectedIntervalOption.memberPrice : null;

            let sumPrice = selectedIntervalOption
              ? parseFloat(selectedIntervalOption.price)
              : parseFloat(intervalOptions[0].price);
            selectedMembers.forEach(member => {
              const mp = memberPrice
                ? [].concat(memberPrice).find(p => p.memberKey === member.key)
                : null;
              sumPrice += mp ? parseFloat(mp.price.numericValue) : 0;
            });
            plan.price = sumPrice;
          }

          if (intervalOptions && intervalOptions.length === 0) {
            // No Coverage
            plan = lodash.cloneDeep(plan);
            plan.isAvailable = false;
            plan.planId = plan.allIntervalOptions.length
              ? plan.allIntervalOptions[0].optionId
              : plan.planId;
          }
        }

        if (option) {
          const isPlanAvailable = option.isAvailable;

          targetBenefit.dependencyMessage = option.message;
          return {
            ...plan,
            intervalOptions,
            // Apply interval options plan selection based on dependency options above,
            // otherwise fallback to individual plan selection
            isAvailable: isNoCoveragePlan(plan)
              ? isPlanAvailable
              : intervalOptions && intervalOptions.length
              ? plan.isAvailable
              : isPlanAvailable,
            isSelected: isPlanAvailable ? plan.isSelected : false,
          };
        }

        return {
          ...plan,
          intervalOptions,
          isAvailable: true,
        };
      });
    }
  } else if (
    targetBenefit &&
    Array.isArray(dependancyOptions) &&
    Array.isArray(targetBenefit.plans)
  ) {
    // For group plan based
    const flatDependancies = [];

    dependancyOptions.forEach(option => {
      if (option.dependancyBenefitID === targetBenefit.benefitId) {
        flatDependancies[option.optionID] = option;
      }
    });

    targetBenefit.plans.forEach(plan => {
      if (plan.options && Array.isArray(plan.options)) {
        plan.options.forEach(option => {
          const depOption = flatDependancies[option.planId];

          if (depOption) {
            option.isAvailable = depOption.notSelectableDueToBenefitDependancy === "false";

            if (option.isAvailable === false) {
              option.isSelected = false;
            }

            targetBenefit.dependencyMessage = depOption.message;
          }
        });

        // For benefits that are linked to multiple products, the
        // availability of the plan should be based on its availability
        // against all applicable link logic. This is where
        // updatedLinkedBenefits is used for tracking and checking.
        // e.g. sample use case: GTLEE_GTLSP and GTLEE_GCIEE
        plan.isAvailable =
          updatedLinkedBenefits.indexOf(targetBenefit.benefitId) >= 0
            ? plan.isAvailable && checkIfPlanIsAvailable(plan)
            : checkIfPlanIsAvailable(plan);

        if (plan.isAvailable === false) {
          plan.isSelected = false;
        }
      } else {
        const depOption = flatDependancies[plan.planId];

        if (depOption) {
          plan.isAvailable = depOption.notSelectableDueToBenefitDependancy === "false";
          targetBenefit.dependencyMessage = depOption.message;

          if (plan.isAvailable === false) {
            plan.isSelected = false;
          }
        }
      }
    });
  }
};

/*eslint-enable complexity*/
// Temporary Fix to handle interval options without dependency
const updateIntervalOptionsWithoutDependency = data => {
  const { benefit } = data || {};

  if (benefit && Array.isArray(benefit.plans)) {
    // Check availability for each plan
    benefit.plans = benefit.plans.map(plan => {
      // Check for available interval options
      let intervalOptions = null;

      if (plan.allIntervalOptions) {
        // load all interval options if there are no dependecyOptions
        intervalOptions = plan.allIntervalOptions;

        if (intervalOptions.length > 0) {
          // Check for previously selected plan
          const prevPlanSelected = intervalOptions.find(opt => opt.optionId === plan.planId);

          plan.planValue = prevPlanSelected
            ? prevPlanSelected.planValue
            : intervalOptions[0].planValue;
          plan.groupValue = prevPlanSelected
            ? prevPlanSelected.optionId
            : intervalOptions[0].optionId;
          plan.planId = prevPlanSelected ? prevPlanSelected.optionId : intervalOptions[0].optionId;
          plan.sumAssured = prevPlanSelected
            ? prevPlanSelected.sumAssured
            : intervalOptions[0].sumAssured;
          plan.uniqueId = prevPlanSelected
            ? prevPlanSelected.uniqueId
            : `${benefit.benefitId}-${intervalOptions[0].optionId}`;
          plan.isHDFRequired = prevPlanSelected
            ? prevPlanSelected.isHDFRequired
            : intervalOptions[0].isHDFRequired;

          plan.isAvailable = prevPlanSelected
            ? prevPlanSelected.isAvailable
            : intervalOptions[0].isAvailable;

          const selectedMembers =
            plan.eligibleMembers && plan.eligibleMembers.members
              ? plan.eligibleMembers.members.filter(
                  member => member.isSelected && member.type !== "Employee"
                )
              : [];
          const selectedIntervalOption = intervalOptions.find(
            option => option.optionId === plan.planId
          );
          const memberPrice = selectedIntervalOption ? selectedIntervalOption.memberPrice : null;

          let sumPrice = selectedIntervalOption
            ? parseFloat(selectedIntervalOption.price)
            : parseFloat(intervalOptions[0].price);
          selectedMembers.forEach(member => {
            const mp = memberPrice
              ? [].concat(memberPrice).find(p => p.memberKey === member.key)
              : null;
            sumPrice += mp ? parseFloat(mp.price.numericValue) : 0;
          });
          plan.price = sumPrice;
        }

        if (intervalOptions.length === 0) {
          // No Coverage
          plan = lodash.cloneDeep(plan);
          plan.isAvailable = false;
          plan.planId = plan.allIntervalOptions.length
            ? plan.allIntervalOptions[0].optionId
            : plan.planId;
        }
      }

      return {
        ...plan,
        intervalOptions,
        isAvailable: true,
      };
    });
  }
};

export const applyDependancyOptions = (benefits, updatedLinkedBenefitsInit) => {
  const updatedLinkedBenefits = updatedLinkedBenefitsInit ? updatedLinkedBenefitsInit : [];
  benefits.forEach(benefit => {
    const selectedPlan = benefit.plans.find(plan => plan.isSelected);

    if (!benefit.parent && !benefit.children) {
      const data = {
        benefits,
        benefit,
        selectedPlan,
      };

      updateDependencyOptions(data);
    }

    if (benefit.parent) {
      const data = {
        benefits,
        benefit,
        selectedPlan,
        parentBenefitId: benefit.parent.parentBenefitId,
        linkLogicType: benefit.parent.linkLogicType,
        dependency: benefit.parent.dependency,
      };

      updateDependencyOptions(data, updatedLinkedBenefits);
      updatedLinkedBenefits.push(benefit.parent.parentBenefitId);
    }
    let childBenefitAllLinkedBenefitParent = true;

    for (const childBenefit of benefit.children) {
      if (
        !childBenefit.isLinkedBenefitParentBenefit ||
        childBenefit.isLinkedBenefitParentBenefit === "false"
      ) {
        childBenefitAllLinkedBenefitParent = false;
        const data = {
          benefits,
          benefit,
          selectedPlan,
          childBenefitId: childBenefit.benefitId,
          linkLogicType: childBenefit.linkLogicType,
          dependency: childBenefit.dependency,
        };

        updateDependencyOptions(data, updatedLinkedBenefits);
        updatedLinkedBenefits.push(childBenefit.benefitId);
      }
    }

    // Check if benefit has interval options regardless if it has dependencies
    const hasIntervalOptions = benefit.plans.some(p => p.hasOwnProperty("allIntervalOptions"));

    if (
      hasIntervalOptions &&
      !benefit.parent &&
      (childBenefitAllLinkedBenefitParent || !benefit.children || benefit.children.length === 0)
    ) {
      const data = {
        benefit,
        selectedPlan,
      };

      updateIntervalOptionsWithoutDependency(data);
    }
  });
};

export const applyPerMemberDependencyOptions = benefits => {
  benefits.forEach(benefit => {
    if (benefit.selectionMode === "individual" && benefit.children) {
      const selectedPlan = benefit.plans.find(plan => plan.isSelected);
      riskmedicalPerMemberUtils.applyPerMemberDependencySelection(benefits, benefit, selectedPlan);
    }
  });
};

const getParentDependency = benefitData => {
  const rawBenefitOptions = benefitData.find(element => element.id === "BenefitOptions");
  const parentBenefitId = rawBenefitOptions.parentBenefitID;

  if (parentBenefitId) {
    let dependency = rawBenefitOptions.rHSDependancyType;

    if (
      linkLogicRelationships[dependency] === linkLogicRelationships.By1To1MatchPlanID &&
      rawBenefitOptions.showIncludeExclude === "true"
    ) {
      dependency = "By1To1MatchPlanIdOrNoCoverage";
    }

    const linkLogicType = linkLogicRelationships[dependency];

    return {
      parentBenefitId,
      linkLogicType,
      dependency,
    };
  }

  return null;
};

// Generate child benefits IDs
// option - Contain the plan options of the current selected Benefit.
const getDependencyOptionsChildBenefitIds = option => {
  let benefitIds = [];

  const setDependencyOptions = options => {
    const dependencyOptions = [].concat(options);

    dependencyOptions.forEach(dependancyOption => {
      if (!benefitIds.includes(dependancyOption.benefitID)) {
        benefitIds.push(dependancyOption.benefitID);
      }
    });
  };

  if (Array.isArray(option)) {
    option.forEach(plan => {
      if (plan.dependancyOptions) {
        setDependencyOptions(plan.dependancyOptions);
      }
    });

    return benefitIds.length > 0 ? benefitIds : null;
  }

  if (option && option.dependancyOptions) {
    setDependencyOptions(option.dependancyOptions);

    return benefitIds.length > 0 ? benefitIds : null;
  }

  return null;
};

const getChildDependenciesIsLinkParentBenefit = option => {
  let benefitIds = [];
  let isLinkParentBenefit;
  const setIsLinkParentBenefit = options => {
    const dependencyOptions = [].concat(options);

    dependencyOptions.forEach(dependancyOption => {
      if (!benefitIds.includes(dependancyOption.benefitID)) {
        benefitIds.push(dependancyOption.benefitID);
        isLinkParentBenefit = dependancyOption.isLinkedBenefitParentBenefit
          ? dependancyOption.isLinkedBenefitParentBenefit
          : null;
      }
    });
  };

  if (Array.isArray(option)) {
    option.forEach(plan => {
      if (plan.dependancyOptions) {
        setIsLinkParentBenefit(plan.dependancyOptions);
      }
    });
  }

  if (option && option.dependancyOptions) {
    setIsLinkParentBenefit(option.dependancyOptions);
  }
  return isLinkParentBenefit;
};

const getChildDependencies = (benefitData, benefits) => {
  const rawBenefitOptions = benefitData.find(element => element.id === "BenefitOptions");

  const rawChildBenefitIds = rawBenefitOptions.rHSBenefitID;
  const dependencies = [];

  // If there are no child benefit ids return by the API, do a lookup at dependency options
  // and compose the child ids if have.
  const childBenefitIds = rawChildBenefitIds
    ? rawChildBenefitIds.split(",")
    : getDependencyOptionsChildBenefitIds(rawBenefitOptions.option);

  if (!childBenefitIds) {
    return dependencies;
  }

  for (const childBenefitId of childBenefitIds) {
    const childBenefit = benefits.find(benefit => benefit.benefitID === childBenefitId);

    if (childBenefit) {
      const childDependency = getParentDependency(childBenefit.data);
      const isLinkedBenefitParentBenefit = getChildDependenciesIsLinkParentBenefit(
        rawBenefitOptions.option
      );
      if (childDependency) {
        childDependency.benefitId = childBenefitId;
        if (isLinkedBenefitParentBenefit) {
          childDependency.isLinkedBenefitParentBenefit = isLinkedBenefitParentBenefit;
        }
        dependencies.push(childDependency);
      }
    }
  }

  return dependencies;
};

/*eslint-disable complexity*/
const updatePlan = (benefits, benefitId, plan, selectOriginal) => {
  if (!plan) {
    return benefits;
  }

  const updatedLinkedBenefits = [];
  const currentBenefit = benefits.find(benefit => benefit.benefitId === benefitId);

  updateBenefitFilter(currentBenefit, plan, true);
  let selectedOptions;
  let selectedPlan;

  if (plan.options && Array.isArray(plan.options)) {
    const filteredOptions = lodash.filter(plan.options, option => option.isAvailable === true);

    selectedOptions = lodash.filter(filteredOptions, option => option.isSelected === true);
    if (
      filteredOptions &&
      selectedOptions &&
      filteredOptions.length > 0 &&
      selectedOptions.length === 0
    ) {
      //Need to select last available option:
      const lastOption = filteredOptions[0];

      lastOption.isSelected = true;

      selectedOptions = [lastOption];
    }
  }
  plan.isSelected = true;

  // Find selected plan:
  if (Array.isArray(currentBenefit.plans)) {
    currentBenefit.plans.forEach(currentPlan => {
      // Update selected plan based on interval options
      if (currentPlan.intervalOptions && currentPlan.intervalOptions.length) {
        const intervalOptionFound = currentPlan.intervalOptions.find(
          option => option.uniqueId === plan.uniqueId
        );
        if (intervalOptionFound) {
          const {
            optionId,
            uniqueId,
            isHDFRequired,
            planValue,
            sumAssured,
            price,
            memberPrice,
          } = intervalOptionFound;
          const selectedMembers =
            currentPlan.eligibleMembers && currentPlan.eligibleMembers.members
              ? currentPlan.eligibleMembers.members.filter(
                  member => member.isSelected && member.type !== "Employee"
                )
              : [];
          let sumPrice = parseFloat(price);
          selectedMembers.forEach(member => {
            const mp = memberPrice
              ? [].concat(memberPrice).find(p => p.memberKey === member.key)
              : null;
            sumPrice += mp ? parseFloat(mp.price.numericValue) : 0;
          });
          // TODO must mutate, do not overwrite immutable objects
          currentPlan.planId = optionId;
          currentPlan.planValue = planValue;
          // currentPlan.groupValue = optionId;
          currentPlan.uniqueId = uniqueId;
          currentPlan.isHDFRequired = isHDFRequired;
          currentPlan.sumAssured = sumAssured;
          currentPlan.price = accounting.toFixed(sumPrice, 2);
          currentPlan.isAvailable = checkIfPlanIsAvailable(plan);
          currentPlan.isSelected = checkIfPlanIsSelected(plan);
          currentPlan.dependencyMessage = plan.dependencyMessage;

          selectedPlan = currentPlan;
        } else {
          // if there's no interval found, reset previously selected plan
          currentPlan.isSelected = false;
        }
      } else if (currentPlan.uniqueId === plan.uniqueId) {
        // Update selected plan:
        currentPlan.isAvailable = checkIfPlanIsAvailable(plan);
        currentPlan.isSelected = checkIfPlanIsSelected(plan);
        currentPlan.dependencyMessage = plan.dependencyMessage;
        if (plan.options && Array.isArray(plan.options)) {
          currentPlan.options = plan.options.map(a => {
            // const isSelectedOption = selectedOptions.find(option => option.optionId = a.optionId);
            // if(isSelectedOption){
            //   return Object.assign({isSelected:isSelectedOption.isSelected}, a);
            // }
            return lodash.cloneDeep(a);
          });
        }
        if (plan.eligibleMembers) {
          currentPlan.eligibleMembers = plan.eligibleMembers;
          const member =
            currentPlan.eligibleMembers && currentPlan.eligibleMembers.length
              ? currentPlan.eligibleMembers[0]
              : currentPlan.eligibleMembers.members && currentPlan.eligibleMembers.members.length
              ? currentPlan.eligibleMembers.members[0]
              : null;
          const selectionMode = member ? member.selectionMode : "";
          if (selectionMode && selectionMode.toLowerCase() === "individual") {
            const selectedMembers = currentPlan.eligibleMembers.members.filter(
              m => (m.isSelected || m.type === "Employee") && m.price
            );
            currentPlan.price = selectedMembers.reduce((acc, m) => {
              const price = currentPlan.isRelativePrice
                ? parseFloat(m.relativePrice.numericValue)
                : parseFloat(m.price.numericValue);
              return acc + price;
            }, 0);
          }
        }
        selectedPlan = currentPlan;
      } else {
        // Update deselected plan:
        resetPlanSelection(currentPlan, selectedOptions);
      }
    });
  }

  // To prevent recursive loop:
  currentBenefit.isUpdated = true;
  if (!currentBenefit.parent && !currentBenefit.children) {
    const data = {
      benefits,
      currentBenefit,
      selectedPlan,
    };
    updateDependencyOptions(data);
  }

  if (currentBenefit.parent) {
    const data = {
      benefits,
      currentBenefit,
      selectedPlan,
      parentBenefitId: currentBenefit.parent.parentBenefitId,
      linkLogicType: currentBenefit.parent.linkLogicType,
      dependency: currentBenefit.parent.dependency,
    };

    updateDependencyOptions(data, updatedLinkedBenefits);
    updatedLinkedBenefits.push(currentBenefit.parent.parentBenefitId);
  }

  for (const childBenefit of currentBenefit.children) {
    if (
      !childBenefit.isLinkedBenefitParentBenefit ||
      childBenefit.isLinkedBenefitParentBenefit === "false"
    ) {
      const data = {
        benefits,
        currentBenefit,
        selectedPlan,
        childBenefitId: childBenefit.benefitId,
        linkLogicType: childBenefit.linkLogicType,
        dependency: childBenefit.dependency,
      };
      updateDependencyOptions(data, updatedLinkedBenefits);
      updatedLinkedBenefits.push(childBenefit.benefitId);
    }
  }
  // Call applyDependancyOptions to be consistent with logic applied on initial load of data
  if (currentBenefit.parent || (currentBenefit.children && currentBenefit.children.length > 0)) {
    applyDependancyOptions(benefits, updatedLinkedBenefits);
  }

  // apply parent product member selection to children product member
  const { eligibleMembers: { members = null } = {} } = selectedPlan || {};
  const selectionMode =
    members && members.length ? members[0].selectionMode : members ? members.selectionMode : "";
  if (
    members &&
    selectionMode &&
    selectionMode.toLowerCase() === "individual" &&
    selectedPlan.options
  ) {
    const selectionMode = members[0].selectionMode.toLowerCase();
    selectedPlan.options = riskmedicalPerMemberUtils.getPlanByMemberSelection({
      options: selectedPlan.options,
      membersTemp: members,
      selectionMode,
    });
  }

  if (selectedPlan && !selectOriginal) {
    benefits = currentBenefit.updateBenefits(benefits, currentBenefit, selectedPlan);

    return benefits;
  }

  return benefits;
};

/*eslint-enable complexity*/
const findValidPlan = (benefits, benefit, newPlans, parentPlan) => {
  // Check if plan with same group has already been selected
  const selectedPlan = benefit.plans.find(plan => checkIfPlanIsSelected(plan));
  const parentGroupValue = parentPlan.groupValue;

  // By default select EE or EO
  let newPlan = newPlans.find(
    plan => plan.groupValue === parentGroupValue && checkIfPlanIsAvailableByValue(plan, "1")
  );

  if (!newPlan) {
    newPlan = newPlans.find(plan => checkIfPlanIsAvailableByValue(plan, "1"));
  }

  if (!newPlan && parentPlan) {
    const parentPlanValue = getSelectedPlanValue(parentPlan);

    // Same plan value or same dependent option
    newPlan = newPlans.find(plan => checkIfPlanIsAvailableByValue(plan, parentPlanValue));
  }

  if (!newPlan) {
    // No coverage
    newPlan = newPlans.find(plan => checkIfPlanIsAvailableByValue(plan, "0"));
  }

  if (!newPlan && newPlans && newPlans.length > 0) {
    // Select the last available plan:
    newPlan = newPlans[newPlans.length - 1];
  }

  if (
    selectedPlan &&
    newPlan &&
    getSelectedPlanValue(selectedPlan) === getSelectedPlanValue(newPlan) &&
    selectedPlan.groupValue === newPlan.groupValue
  ) {
    newPlan = selectedPlan;
  }

  if (selectedPlan) {
    // TODO: Check which link logic is affecting this change
    const existingPlan = newPlans.find(
      plan =>
        plan.uniqueId === selectedPlan.uniqueId &&
        plan.groupValue === selectedPlan.groupValue &&
        getSelectedPlanValue(plan) === getSelectedPlanValue(selectedPlan)
    );
    if (existingPlan) {
      // TODO:
      // When return newPlan, causing abnormal selection behaviour of benefit
      newPlan = existingPlan;
    }
  }

  return newPlan;
};

const findAndSelectValidPlan = (benefits, benefit, newPlans, parentPlan, copyOptions) => {
  let newPlan = findValidPlan(benefits, benefit, newPlans, parentPlan);
  // Check if plan with same group has already been selected
  const selectedPlan = benefit.plans.find(plan => checkIfPlanIsSelected(plan));

  if (copyOptions && newPlan && newPlan.options && selectedPlan) {
    let options = selectedPlan.options;

    if (!Array.isArray(options)) {
      options = [options];
    }

    newPlan.options = options.map(a => lodash.cloneDeep(a));
  }

  if (newPlan) {
    // Save previous selection state if any:
    newPlan = lodash.cloneDeep(newPlan);
  }

  // No plan with same group has been selected before. Need to set a new plan.
  // Reset current selection:

  resetBenefitSelection(benefit);

  if (newPlan) {
    return updatePlan(benefits, benefit.benefitId, newPlan, false);
  }

  return benefits;
};

const updateBenefitsByOneToOneMatchPlanId = data => {
  const { selectedPlan, benefits } = data;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);

  // 1. Filter plans
  const availablePlans = targetBenefit.plans.filter(plan => checkIfPlanIsAvailable(plan));

  // 2. Find new plans
  const newPlans = availablePlans.filter(plan => plan.planId === selectedPlan.planId);

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan, true);
};

const updateBenefitsByOneToOneMatchPlanIdOrNoCoverage = data => {
  const { selectedPlan, benefits } = data;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);

  if (data.childBenefitId) {
    const childSelectedPlan = targetBenefit.plans.find(plan => checkIfPlanIsSelected(plan));

    if (childSelectedPlan) {
      if (isNoCoveragePlan(childSelectedPlan)) {
        //No coverage is already selected. No need to change
        updateBenefitFilter(targetBenefit, data.selectedPlan, false);
        return benefits;
      }
    }
  } else if (isNoCoveragePlan(data.selectedPlan)) {
    //No coverage is already selected. No need to change
    return benefits;
  }

  // 1. Filter plans
  const availablePlans = targetBenefit.plans.filter(plan => checkIfPlanIsAvailable(plan));

  // 2. Find new plans
  const newPlans = availablePlans.filter(plan => plan.planId === selectedPlan.planId);

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan, true);
};

const updateBenefitsBySameGroupValueOrNoCoverage = data => {
  const { benefits } = data;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedGroupValue = data.selectedPlan.groupValue;

  if (data.childBenefitId) {
    const childSelectedPlan = targetBenefit.plans.find(plan => checkIfPlanIsSelected(plan));

    if (childSelectedPlan) {
      if (isNoCoveragePlan(childSelectedPlan)) {
        //No coverage is already selected. No need to change
        updateBenefitFilter(targetBenefit, data.selectedPlan, false);

        return benefits;
      }
    }
  } else if (isNoCoveragePlan(data.selectedPlan)) {
    //No coverage is already selected. No need to change
    return benefits;
  }

  // 1. Find new plans
  const newPlans = targetBenefit.plans.filter(
    plan => plan.groupValue === selectedGroupValue || (plan.isAvailable && isNoCoveragePlan(plan))
  );

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const parseNumValue = value => {
  if (typeof value !== "number") {
    return parseFloat(value);
  }
  return value;
};

const plansByGreaterSumAssured = (source, sumAssured) =>
  source && source.isAvailable && parseNumValue(source.sumAssured) >= parseNumValue(sumAssured);

const plansByLessSumAssured = (source, sumAssured) =>
  source && source.isAvailable && parseNumValue(source.sumAssured) <= parseNumValue(sumAssured);

const plansByGreaterPlanValue = (source, planValue) =>
  source.isAvailable && parseNumValue(source.planValue) >= parseNumValue(planValue);
const plansByLessPlanValue = (source, planValue) =>
  source.isAvailable && parseNumValue(source.planValue) <= parseNumValue(planValue);

const updateBenefitsBySameGroupValueOrNoCoverageAndLessEqualPlanValue = data => {
  const { benefits } = data;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedGroupValue = data.selectedPlan.groupValue;
  const selectedPlanValue = getSelectedPlanValue(data.selectedPlan);
  const noCoveragePlan = targetBenefit.plans.find(plan => isNoCoveragePlan(plan));
  const comparator = data.parentBenefitId ? plansByGreaterPlanValue : plansByLessPlanValue;

  if (data.childBenefitId) {
    const childSelectedPlan = targetBenefit.plans.find(plan => checkIfPlanIsSelected(plan));

    if (childSelectedPlan) {
      if (isNoCoveragePlan(childSelectedPlan)) {
        //No coverage is already selected. No need to change
        updateBenefitFilter(targetBenefit, data.selectedPlan, false);

        return benefits;
      }
    }
  } else if (isNoCoveragePlan(data.selectedPlan)) {
    //No coverage is already selected. No need to change
    return benefits;
  }

  // Find new plans with same group
  const plansWithSameGroup = targetBenefit.plans.filter(
    plan => plan.groupValue === selectedGroupValue
  );

  // Find new plans with correct plan value
  let newPlans = plansWithSameGroup.filter(plan => {
    if (plan.options && Array.isArray(plan.options)) {
      const options = plan.options.filter(option => comparator(option, selectedPlanValue));

      return options && options.length > 0;
    }
    return comparator(plan, selectedPlanValue);
  });

  // Add "No Coverage" plan
  if (noCoveragePlan && !newPlans.find(plan => isNoCoveragePlan(plan))) {
    newPlans.splice(0, 0, noCoveragePlan);
  }

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsByPlanValue = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedPlanValue = getSelectedPlanValue(data.selectedPlan);

  const filterPlansByPlanValue = (plans, planValue, comparator) => {
    return plans.filter(plan => {
      if (plan.options && Array.isArray(plan.options)) {
        const options = plan.options.filter(option => comparator(option, planValue));

        return options && options.length > 0;
      }
      return comparator(plan, planValue);
    });
  };

  const comparator = data.parentBenefitId ? plansByGreaterPlanValue : plansByLessPlanValue;

  // Find new plans
  const newPlans = filterPlansByPlanValue(targetBenefit.plans, selectedPlanValue, comparator);

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsByEqualPlanValue = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedPlanValue = getSelectedPlanValue(data.selectedPlan);

  // Find new plans
  let newPlans = targetBenefit.plans.filter(plan =>
    checkIfPlanIsAvailableByValue(plan, selectedPlanValue)
  );

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsByGroupValue = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedGroupValue = data.selectedPlan.groupValue;

  // Find new plans
  const newPlans = targetBenefit.plans.filter(plan =>
    data.parentBenefitId
      ? plan.groupValue >= selectedGroupValue
      : plan.groupValue <= selectedGroupValue
  );

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsByOneToOneMatchPlanGroupValue = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedGroupValue = data.selectedPlan.groupValue;

  // Find new plans
  const newPlans = targetBenefit.plans.filter(plan =>
    data.parentBenefitId
      ? plan.groupValue === selectedGroupValue
      : plan.groupValue === selectedGroupValue
  );

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsByPlanAndGroupValue = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedPlanValue = getSelectedPlanValue(data.selectedPlan);
  const selectedGroupValue = data.selectedPlan.groupValue;

  const comparator = data.parentBenefitId ? plansByGreaterPlanValue : plansByLessPlanValue;

  // Find new plans
  const plansWithSameGroup = targetBenefit.plans.filter(plan =>
    data.parentBenefitId
      ? plan.groupValue >= selectedGroupValue
      : plan.groupValue <= selectedGroupValue
  );

  const newPlans = lodash.filter(plansWithSameGroup, plan => {
    if (plan.options && Array.isArray(plan.options)) {
      const options = plan.options.filter(option => comparator(option, selectedPlanValue));

      return options && options.length > 0;
    }
    return comparator(plan, selectedPlanValue);
  });

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsBySumAssuredAmount = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedSumAssured = getSelectedSumAssured(data.selectedPlan);

  const comparator = data.parentBenefitId ? plansByGreaterSumAssured : plansByLessSumAssured;

  const filterPlansBySumAssured = (plans = [], sumAssured, comparator) => {
    return plans.filter(plan => {
      if (
        plan.intervalOptions &&
        Array.isArray(plan.intervalOptions) &&
        plan.intervalOptions.length > 0
      ) {
        const intervalOptions = plan.intervalOptions.filter(option =>
          comparator(option, sumAssured)
        );

        return intervalOptions && intervalOptions.length > 0;
      }

      return comparator(plan, sumAssured);
    });
  };

  if (isNoCoveragePlan(data.selectedPlan)) {
    //No coverage is already selected. No need to change
    updateIntervalBenefits(data);

    //return benefits;
  }

  const targetPlans = targetBenefit ? targetBenefit.plans : [];

  // Find new plans
  const newPlans = filterPlansBySumAssured(targetPlans, selectedSumAssured, comparator);

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsBySumAssuredAmountGreater = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedSumAssured = data.selectedPlan.sumAssured;

  const comparator = data.parentBenefitId ? plansByLessSumAssured : plansByGreaterSumAssured;

  const filterPlansBySumAssured = (plans = [], sumAssured, comparator) => {
    return plans.filter(plan => {
      if (
        plan.intervalOptions &&
        Array.isArray(plan.intervalOptions) &&
        plan.intervalOptions.length > 0
      ) {
        const intervalOptions = plan.intervalOptions.filter(option =>
          comparator(option, sumAssured)
        );

        return intervalOptions && intervalOptions.length > 0;
      }

      return comparator(plan, sumAssured);
    });
  };

  if (isNoCoveragePlan(data.selectedPlan)) {
    //No coverage is already selected. No need to change
    updateIntervalBenefits(data);

    //return benefits;
  }

  const targetPlans = targetBenefit ? targetBenefit.plans : [];

  // Find new plans
  const newPlans = filterPlansBySumAssured(targetPlans, selectedSumAssured, comparator);

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const updateBenefitsBySameGroupValueAndLessEqualPlanValue = data => {
  const benefits = data.benefits;
  const targetBenefitId = data.parentBenefitId ? data.parentBenefitId : data.childBenefitId;
  const targetBenefit = benefits.find(benefit => benefit.benefitId === targetBenefitId);
  const selectedPlanValue = getSelectedPlanValue(data.selectedPlan);

  const comparator = data.parentBenefitId ? plansByGreaterPlanValue : plansByLessPlanValue;

  // 1. Find new plan
  const plansWithSameGroup = targetBenefit.plans.filter(
    plan => plan.groupValue == data.selectedPlan.groupValue
  );
  const newPlans = lodash.filter(plansWithSameGroup, plan => {
    if (plan.options && Array.isArray(plan.options)) {
      const options = plan.options.filter(option => comparator(option, selectedPlanValue));

      return options && options.length > 0;
    }
    return comparator(plan, selectedPlanValue);
  });

  return findAndSelectValidPlan(benefits, targetBenefit, newPlans, data.selectedPlan);
};

const applyLinkLogic = data => {
  switch (data.linkLogicType) {
    case linkLogicRelationships.By1To1MatchPlanID:
      data.benefits = updateBenefitsByOneToOneMatchPlanId(data);
      break;
    case linkLogicRelationships.By1To1MatchPlanIdOrNoCoverage:
      data.benefits = updateBenefitsByOneToOneMatchPlanIdOrNoCoverage(data);
      break;
    case linkLogicRelationships.BySameGroupValueOrNoCoverage:
      data.benefits = updateBenefitsBySameGroupValueOrNoCoverage(data);
      break;
    case linkLogicRelationships.BySameGroupValueOrNoCoverageAndLessEqualPlanValue:
      data.benefits = updateBenefitsBySameGroupValueOrNoCoverageAndLessEqualPlanValue(data);
      break;
    case linkLogicRelationships.ByPlanValue:
      data.benefits = updateBenefitsByPlanValue(data);
      break;
    case linkLogicRelationships.ByEqualPlanValue:
      data.benefits = updateBenefitsByEqualPlanValue(data);
      break;
    case linkLogicRelationships.ByPlanAndGroupValue:
      data.benefits = updateBenefitsByPlanAndGroupValue(data);
      break;
    case linkLogicRelationships.ByGroupValue:
      data.benefits = updateBenefitsByGroupValue(data);
      break;
    case linkLogicRelationships.By1To1MatchPlanGroupValue:
      data.benefits = updateBenefitsByOneToOneMatchPlanGroupValue(data);
      break;
    case linkLogicRelationships.BySumAssuredAmount:
      data.benefits = updateBenefitsBySumAssuredAmount(data);
      break;
    case linkLogicRelationships.BySumAssuredAmountGreater:
      data.benefits = updateBenefitsBySumAssuredAmountGreater(data);
      break;
    case linkLogicRelationships.BySameGroupValueAndLessEqualPlanValue:
      data.benefits = updateBenefitsBySameGroupValueAndLessEqualPlanValue(data);
      break;
    default:
      break;
  }
  return data.benefits;
};

const updateBenefits = (oldBenefits, selectedBenefit, selectedPlan) => {
  let benefits = oldBenefits.map(a => Object.assign({}, a));
  //update dependent benefit plans per-member before apply link logic, but it will not update the not-available plan
  if (selectedBenefit.selectionMode === "individual") {
    riskmedicalPerMemberUtils.applyPerMemberDependencySelection(
      benefits,
      selectedBenefit,
      selectedPlan
    );
  }

  if (selectedBenefit.isUpdated) {
    return benefits;
  }

  // Nothing to do - just update current state:
  benefits = updatePlan(benefits, selectedBenefit.benefitId, selectedPlan, true);

  if (!selectedBenefit.parent && !selectedBenefit.children) {
    return benefits;
  }

  if (selectedBenefit.parent) {
    const data = {
      benefits,
      selectedBenefit,
      selectedPlan,
      parentBenefitId: selectedBenefit.parent.parentBenefitId,
      linkLogicType: selectedBenefit.parent.linkLogicType,
      dependency: selectedBenefit.parent.dependency,
    };
    benefits = applyLinkLogic(data);
  }

  for (const childBenefit of selectedBenefit.children) {
    if (
      !childBenefit.isLinkedBenefitParentBenefit ||
      childBenefit.isLinkedBenefitParentBenefit === "false"
    ) {
      const data = {
        benefits,
        selectedBenefit,
        selectedPlan,
        childBenefitId: childBenefit.benefitId,
        linkLogicType: childBenefit.linkLogicType,
        dependency: childBenefit.dependency,
      };
      benefits = applyLinkLogic(data);
    }
  }
  //update dependent benefit available plans per-member
  if (selectedBenefit.selectionMode === "individual") {
    riskmedicalPerMemberUtils.applyPerMemberDependencySelection(
      benefits,
      selectedBenefit,
      selectedPlan
    );
  }
  return benefits;
};

// Data transformation

// Extract item from an array
const getDataObject = (data, id) => data.reduce((obj, item) => (item.id === id ? item : obj), {});

const getSumAssuredPriceTagSetting = (plan, priceTagDisplay, pricing) => {
  const settings = {};
  const relativePrice = priceTagDisplay.priceTagMode === "RelativePrice";
  const members =
    plan.eligibleMembers && plan.eligibleMembers.length
      ? plan.eligibleMembers
      : plan.eligibleMembers.members && plan.eligibleMembers.members.length
      ? plan.eligibleMembers.members
      : [];
  const selectionMode = members && members.length ? members[0].selectionMode : "";

  let relativePriceAmount;

  settings.relativeTagNode = "";

  if (relativePrice) {
    relativePriceAmount = pricing.relativePrice
      ? parseFloat(pricing.relativePrice.numericValue).toFixed(2)
      : "0.00";
    settings.relativeTagNode = pricing.relativePrice ? pricing.relativePrice.relativeTagNode : "";
  }

  // Always use actual price for cart

  settings.isRelativePrice = relativePrice;
  settings.relativePrice = relativePriceAmount;
  if (selectionMode && selectionMode.toLowerCase() === "individual") {
    const selectedMembers = members ? members.filter(m => m.isSelected) : [];
    const selectedIntervalOption = plan.intervalOptions
      ? plan.intervalOptions.find(option => option.optionId === plan.planId)
      : null;
    let price = 0;
    if (selectedIntervalOption) {
      const memberPrice = []
        .concat(selectedIntervalOption.memberPrice)
        .filter(mp => selectedMembers.find(member => member.key === mp.memberKey));
      memberPrice.forEach(mp => {
        price += parseFloat(mp.price.numericValue);
      });
    }
    settings.price = price;
  } else {
    const price = parseFloat(pricing.price.numericValue).toFixed(2);
    settings.price = price;
  }

  return { ...plan, ...settings };
};

const getPlanBasedPriceTagSetting = (priceTagDisplay, member, planType) => {
  const settings = {};
  const relativePrice = priceTagDisplay.priceTagMode === "RelativePrice";
  const addEmployeePrice = planType ? planType === "Group" : false;

  let price = 0,
    relativePriceAmount = 0;

  settings.relativeTagNode = "";

  if (Array.isArray(member)) {
    // price = parseFloat(lodash.sumBy(member, member => {
    //   if (relativePrice) {
    //     settings.relativeTagNode = member.relativePrice ? member.relativePrice.relativeTagNode : "";
    //     return member.relativePrice ? member.relativePrice.numericValue : "0";
    //   }

    //   return member.price.numericValue;
    // })).toFixed(2);
    member.forEach(memberObj => {
      if (relativePrice) {
        settings.relativeTagNode = memberObj.relativePrice
          ? memberObj.relativePrice.relativeTagNode
          : "";

        relativePriceAmount += parseFloat(
          memberObj.relativePrice ? memberObj.relativePrice.numericValue : "0"
        );
        price += parseFloat(memberObj.price.numericValue);
      } else if (addEmployeePrice) {
        price += parseFloat(memberObj.price.numericValue);
      } else if (!addEmployeePrice && memberObj.type !== "Employee") {
        price += parseFloat(memberObj.price.numericValue);
      }
    });
  } else if (!Array.isArray(member)) {
    if (relativePrice) {
      settings.relativeTagNode = member.relativePrice ? member.relativePrice.relativeTagNode : "";
      relativePriceAmount = parseFloat(
        member.relativePrice ? member.relativePrice.numericValue : "0"
      );
    } else {
      price = parseFloat(member.price.numericValue);
    }
  }

  settings.isRelativePrice = relativePrice;
  settings.relativePrice = relativePriceAmount;
  settings.price = price.toFixed(2);

  return settings;
};

const getPlanBasedPlanSetting = (plan, priceTagDisplay, planType) => {
  const members = plan.eligibleMembers.members
    ? plan.eligibleMembers.members
    : plan.eligibleMembers.member;
  const selectedMembers = []
    .concat(members)
    .filter(m => (m.isSelected || m.type === "Employee") && m.price);
  const settings = getPlanBasedPriceTagSetting(priceTagDisplay, selectedMembers, planType);
  return { ...plan, ...settings };
};

// Auto select other plan options on page load
// selectedPlan - contain selected plan.
// plans - contain list of plan
const autoSelectOtherPlanOption = (selectedPlan = {}, plans = []) => {
  const selectedPlanOption =
    selectedPlan &&
    selectedPlan.options &&
    selectedPlan.options.find(option => {
      const selectedOption = selectedPlan.options.find(selectedOption => selectedOption.isSelected);

      return option.planId === selectedOption.planId;
    });

  if (selectedPlanOption) {
    plans.forEach(plan => {
      plan.options.forEach(option => {
        // Check option type not equal to No coverage - 00 and auto select plan option
        option.isSelected =
          selectedPlanOption.type !== "00" && selectedPlanOption.type === option.type;
      });
    });
  }
};

// Check whether employee only
const isEmployeeOnly = coverMemberType => {
  const memberTypeList = coverMemberType.split(",");
  let isEO = false;

  if (memberTypeList.length === 1) {
    isEO = memberTypeList.find(
      member =>
        member === "EE" || member === "EO" || member === "SO" || member === "CO" || member === "DO"
    );

    return typeof isEO !== "undefined";
  }

  return false;
};

const getLinkedProductGroupName = (benefitsData, linkedBenefitId) => {
  if (!linkedBenefitId) {
    return null;
  }
  const benefit = benefitsData[linkedBenefitId];
  const planId =
    benefit.selectedOption.option.id !== ""
      ? benefit.selectedOption.option.id
      : benefit.defaultOption.option.id;
  const options = [].concat(benefitsData[linkedBenefitId].benefitOptions.options.option);
  const foundPlan = options.flatMap(option => Array.isArray(option.plan) ? option.plan : [option.plan])
  .find(plan => plan.id === planId);

  if (foundPlan) {
    if (foundPlan.groupName === "No Coverage") {
      return "";
    }
    return foundPlan.groupName;
  }
  return "";
};

// Interval options transformation
const transformIntervalOptions = (
  { intervalOptions: { option = [] } = {}, id },
  benefitId,
  defaultPlanId
) => {
  return option.map(opt => {
    const { planValue, price, requiresUW, interval, name, ...rest } = opt;
    const optionId = `${id}${interval}`;

    return {
      price: price.numericValue,
      planValue: planValue.numericValue,
      sumAssured: planValue.numericValue,
      isHDFRequired: requiresUW === "true",
      optionId,
      name,
      isAvailable: true,
      uniqueId: `${benefitId}-${id}${interval}`,
      isDefault: optionId === defaultPlanId,
      ...rest,
    };
  });
};

// Check for interval options
const isIntervalOptions = option => option.id.slice(-1) === "i" && option.intervalOptions;

// Benefit transformation
const transformBenefits = (data, benefitsData, uiSelection) => {
  let {
    benefits: { ...benefitsRest },
  } = data;
  let {
    benefits: {
      dataListing: { record },
    },
  } = data;

  if (!Array.isArray(record)) {
    record = [record];
  }

  // const transformBenefitDataKeys = ({ data, ...rest }) => data.reduce((obj, item) => ({ ...obj, [camelize(item.id)]: item }), rest);
  // const benefitsData = record.reduce((obj, item) => ({ ...obj, [item.benefitID]: transformBenefitDataKeys(item) }), {});

  // Load Default/Selected/Recommendation options
  // And construct the plans/currentFilter/filters
  /*eslint-disable complexity*/
  const loadBenefitSaveData = (
    lastSelectedPlan,
    data,
    benefitId,
    benefitsData,
    benefitsRest,
    uiSelection
  ) => {
    const benefitOptions = getDataObject(data, "BenefitOptions");
    const selectedOption = getDataObject(data, "SelectedOption");
    const defaultOption = getDataObject(data, "DefaultOption");
    const defaultPlanId = defaultOption.option.id;
    const defaultPlanMembers = defaultOption.option.members;
    const userSelectedPlanId = selectedOption.option.id !== "" ? selectedOption.option.id : null;
    let selectedPlanId = uiSelection !== "Default" ? userSelectedPlanId : defaultPlanId;
    const plans = [];
    const singlePlans = [];
    let savedEligibleMemberWithSinglePlan = [];
    let planSelected = {};

    planSelected = {
      isRecommended: false, // always disabled onload
    };

    if (benefitOptions && benefitOptions.option) {
      const options = [].concat(benefitOptions.option);
      let selectionMode = "";
      options.forEach(option => {
        let planValue = option.id;
        let groupValue = option.id;

        if (
          planValue === "0" ||
          planValue === "0.00" ||
          planValue === "0%" ||
          planValue === "0x" ||
          planValue === "0n" ||
          planValue === "0" ||
          planValue === "0000"
        ) {
          planValue = "0";
          groupValue = "0";
        }
        const first = option.descriptionLine1;
        const second = option.descriptionLine2;

        const description = {
          [true]: option.planDescription || second || first || option.name,
          [!!(first && second)]: `${first} ${second}`,
        }[true];
        selectionMode = (option.eligibleMembers && option.eligibleMembers.length
          ? option.eligibleMembers[0].selectionMode
          : option.eligibleMembers
          ? option.eligibleMembers.selectionMode
          : ""
        ).toLowerCase();
        const eligibleMemberFilteredPlan = riskmedicalPerMemberUtils.getPerMemberConfig({
          planName: option.name,
          isSelected: selectedPlanId === option.id,
          uniqueId: `${benefitId}-${option.id}`,
          planId: option.id,
          isAvailable: true,
          planDescription: description,
          description: first,
          subDescription: second,
          isHDFRequired: option.requiresUW === "true",
          planValue,
          groupValue,
          ...planSelected,
          isDefault: defaultPlanId === option.id,
          defaultPlan: defaultPlanId,
          defaultPlanMembers,
          sumAssured: option.planValue.numericValue,
          dependancyOptions: option.dependancyOptions,
          eligibleMembers: option.eligibleMembers,
          priceLabelName: option.priceLabelName,
        });
        if (isIntervalOptions(option)) {
          const { intervalOptions, ...restOption } = option;
          const options = {
            ...restOption,
            intervalOptions: {
              option: Array.isArray(intervalOptions.option)
                ? intervalOptions.option
                : [].concat(intervalOptions.option),
            },
          };

          eligibleMemberFilteredPlan.allIntervalOptions = transformIntervalOptions(
            options,
            benefitId,
            defaultPlanId
          );
          eligibleMemberFilteredPlan.planValue = option.planValue.numericValue;

          const planSaved = eligibleMemberFilteredPlan.allIntervalOptions.find(
            intervalOpt => intervalOpt.optionId === selectedPlanId
          );

          // restore saved plan
          if (planSaved) {
            eligibleMemberFilteredPlan.planId = planSaved.optionId;
            eligibleMemberFilteredPlan.isSelected = true;
            eligibleMemberFilteredPlan.isDefault = selectedPlanId === defaultPlanId;
          }
        }
        const newPlan = getSumAssuredPriceTagSetting(
          riskmedicalPerMemberUtils.loadPerMemberSavedData(
            lastSelectedPlan,
            selectedOption,
            defaultOption,
            eligibleMemberFilteredPlan,
            selectionMode,
            uiSelection
          ),
          benefitsRest,
          option.pricing
        );
        singlePlans.push(newPlan);
      });

      savedEligibleMemberWithSinglePlan = {
        plans: singlePlans,
        selectionMode,
      };
    } else if (benefitOptions.options) {
      const { groups } = benefitOptions.options;
      let { option } = benefitOptions.options;

      if (!option) {
        option = [];
      }

      if (!Array.isArray(option)) {
        option = [option];
      }

      let planOptionsFilter = option.filter(
        gpOption => gpOption.planList.indexOf(selectedPlanId) > -1
      );

      // No filters, show all plans, no selections
      if (planOptionsFilter.length === 0 && option.length > 0) {
        // Get first plan
        planOptionsFilter = option[0];

        if (Array.isArray(planOptionsFilter.plan)) {
          selectedPlanId = planOptionsFilter.plan[0].id;
        } else {
          selectedPlanId = planOptionsFilter.plan.id;
        }
      }

      const isShowOptions = (element, index, array) =>
        array[index].memberType !== "EE" &&
        array[index].memberType !== "00" &&
        array[index].memberType !== "EO" &&
        array[index].id === selectedPlanId;
      const showOptions = options => {
        if (!Array.isArray(options)) {
          options = [].concat(options);
        }

        return options.some(option => {
          if (Array.isArray(option.plan)) {
            // return option.plan.some(plan => plan.id === selectedPlanId && plan.memberType !== "EE");
            return option.plan.some(isShowOptions);
          }

          return (
            option.plan.id === selectedPlanId &&
            option.plan.memberType !== "EE" &&
            option.plan.memberType !== "00" &&
            option.plan.memberType !== "E0"
          );
        });
      };

      const filterOptions = [];

      if (groups && Array.isArray(groups.group)) {
        groups.group.forEach((el, index) => {
          const messageKey = `filter${el.name.replace(" ", "")}`;

          filterOptions.push({
            messageKey,
            filterName: el.name,
            filterId: `${index}`,
            planGroupName: el.name,
            values: el.groupValues,
          });
        });
      }
      let selectionMode = "";
      if (Array.isArray(option)) {
        option.forEach(planOption => {
          const { plan, groupID, planList, coverMemberType } = planOption;
          const displayGroupName = benefitOptions.displayGroupName === "true";

          if (Array.isArray(plan) && plan.length > 0) {
            planSelected.groupId = groupID;
            const eligibleMembers = [];
            const planOptions = plan.map(p => {
              const option = {
                planId: p.id,
                isSelected: p.id === selectedPlanId,
                type: p.memberType,
                planValue: p.planValue,
                priceLabelName: p.priceLabelName,
                dependancyOptions: p.dependancyOptions,
                isHDFRequired: p.requiresUW === "true"
              };

              eligibleMembers.push(p.eligibleMembers);

              // option.isAvailable = availableDependency;
              return {
                ...option,
                isAvailable: true,
                ...getPlanBasedPriceTagSetting(
                  benefitsRest,
                  p.eligibleMembers.member,
                  benefitOptions.options.planType
                ),
              };
            });
            const planOptionPlan = plan[0]; // Pick a first plan. Usually EE, EO, SO or CO
            const isPlanSelectionOption = planList.indexOf(selectedPlanId) > -1;
            const isPlanDefaultOption = planList.indexOf(defaultPlanId) > -1;
            selectionMode = (eligibleMembers && eligibleMembers.length
              ? eligibleMembers[0].selectionMode
              : eligibleMembers
              ? eligibleMembers.selectionMode
              : ""
            ).toLowerCase();
            // Add memberType EE/EO - to prevent dependant added into plan array. Depdendant should be added into options object
            plans.push(
              getPlanBasedPlanSetting(
                riskmedicalPerMemberUtils.loadPerMemberSavedData(
                  lastSelectedPlan,
                  selectedOption,
                  defaultOption,
                  riskmedicalPerMemberUtils.getPerMemberConfig({
                    planName: planOptionPlan.description,
                    eligibleMembers,
                    planDescription:
                      planOptionPlan.description && planOptionPlan.description.length > 0
                        ? planOptionPlan.description
                        : planOptionPlan.planName,
                    description:
                      planOptionPlan.description && planOptionPlan.description.length > 0
                        ? planOptionPlan.description
                        : planOptionPlan.planName,
                    subDescription: displayGroupName ? planOptionPlan.groupName : null,
                    groupName: planOptionPlan.groupName,
                    groupValue: planOptionPlan.groupValue,
                    planValue: planOptionPlan.planValue,
                    uniqueId: `${benefitId}-${planOptionPlan.id}`,
                    planId: planOptionPlan.id,
                    isSelected: isPlanSelectionOption,
                    isAvailable: true,
                    ...planSelected,
                    isDefault: isPlanDefaultOption,
                    defaultPlan: defaultPlanId,
                    defaultPlanMembers,
                    options: planOptions,
                    isMemberOnly: isEmployeeOnly(coverMemberType),
                    isHDFRequired: planOptionPlan.requiresUW === "true",
                    dependancyOptions: planOptionPlan.dependancyOptions,
                    planType: benefitOptions.options.planType,
                  }),
                  selectionMode,
                  uiSelection
                ),
                benefitsRest,
                benefitOptions.options.planType
              )
            );
          } else if (!Array.isArray(plan)) {
            const planOptions = [];
            planOptions.push({
              planId: plan.id,
              // Should not be selected if not available
              isSelected: plan.id === selectedPlanId,
              isAvailable: true,
              planValue: plan.planValue,
              type: plan.memberType,
              ...getPlanBasedPriceTagSetting(
                benefitsRest,
                plan.eligibleMembers.member,
                benefitOptions.options.planType
              ),
              // price: memberPrice.toFixed(2)
            });

            planSelected.groupId = groupID;
            selectionMode = (plan.eligibleMembers && plan.eligibleMembers.length
              ? plan.eligibleMembers[0].selectionMode
              : plan.eligibleMembers
              ? plan.eligibleMembers.selectionMode
              : ""
            ).toLowerCase();
            plans.push(
              getPlanBasedPlanSetting(
                riskmedicalPerMemberUtils.loadPerMemberSavedData(
                  lastSelectedPlan,
                  selectedOption,
                  defaultOption,
                  riskmedicalPerMemberUtils.getPerMemberConfig({
                    planName: plan.planName,
                    eligibleMembers: plan.eligibleMembers,
                    planDescription:
                      plan.description && plan.description.length > 0
                        ? plan.description
                        : plan.planName,
                    description:
                      plan.description && plan.description.length > 0
                        ? plan.description
                        : plan.planName,
                    subDescription: displayGroupName ? plan.groupName : null,
                    groupName: plan.groupName,
                    groupValue: plan.groupValue,
                    planValue: plan.planValue,
                    uniqueId: `${benefitId}-${plan.id}`,
                    planId: plan.id,
                    // Should not be selected if not available
                    isSelected: plan.id === selectedPlanId,
                    isAvailable: true,
                    ...planSelected,
                    isDefault: plan.id === defaultPlanId,
                    defaultPlan: defaultPlanId,
                    defaultPlanMembers,
                    options: planOptions,
                    isMemberOnly: isEmployeeOnly(coverMemberType),
                    isHDFRequired: plan.requiresUW === "true",
                    dependancyOptions: plan.dependancyOptions,
                    planType: benefitOptions.options.planType,
                  }),
                  selectionMode,
                  uiSelection
                ),
                benefitsRest,
                benefitOptions.options.planType
              )
            );
          }
        });
      }

      const linkedPlanGroupName = getLinkedProductGroupName(benefitsData, benefitOptions.parentBenefitID);
      const selectedPlan = plans.find(plan => plan.isSelected);
      const showIncludeExclude = benefitOptions.showIncludeExclude === "true";
      const displayGroupUI = benefitOptions.displayGroupUI === "true";

      const includeNoneFilters = [
        {
          filterName: "None",
          messageKey: "filterNone",
          filterId: "0",
          planGroupName: "No Coverage",
        },
        {
          filterName: "Include",
          messageKey: "filterInclude",
          filterId: "1",
          planGroupName: linkedPlanGroupName ? linkedPlanGroupName : "",
        },
      ];
      autoSelectOtherPlanOption(selectedPlan, plans);

      // const {
      //   selectionMode,
      //   eligibleMemberFilteredPlans,
      // } = riskmedicalPerMemberUtils.getPerMemberConfig(plans);

      // const savedEligibleMemberWithPlan = riskmedicalPerMemberUtils.loadPerMemberSavedData({
      //   selectionMode,
      //   eligibleMemberFilteredPlans,
      //   selectedOption,
      //   defaultOption,
      // });
      return {
        plans: plans, //eligibleMemberFilteredPlans,
        showIncludeNone: showIncludeExclude,
        filtersIncludeNone: showIncludeExclude && displayGroupUI ? includeNoneFilters : [],
        // For collapse of Add dependent options
        showOptions: showOptions(planOptionsFilter),
        currentGroupName: selectedPlan ? selectedPlan.groupName : "",
        filters: displayGroupUI && filterOptions && filterOptions.length > 1 ? filterOptions : [],
        selectionMode,
      };
    }

    if (lodash.isEmpty(plans)) {
      // filter out plans if the benefit is non selectable
      const targetBenefit = benefitsData && benefitsData[benefitId];

      if (targetBenefit && targetBenefit.selectable === "false") {
        const { selectionMode } = savedEligibleMemberWithSinglePlan;
        const filterSelectedPlan = savedEligibleMemberWithSinglePlan.plans
          .filter(plan => plan.isSelected)
          .map(filterPlan => {
            let obj = {};

            obj = filterPlan;

            if (
              filterPlan.allIntervalOptions &&
              Array.isArray(filterPlan.allIntervalOptions) &&
              filterPlan.allIntervalOptions.length
            ) {
              obj.allIntervalOptions = filterPlan.allIntervalOptions.filter(
                interval => interval.optionId === filterPlan.planId
              );
            }

            return obj;
          });
        return {
          plans: filterSelectedPlan,
          selectionMode,
        };
      }
      return savedEligibleMemberWithSinglePlan;
    }

    return {};
  };

  /*eslint-enable complexity*/
  // Normalize benefit data
  const transformBenefitData = ({ data, ...rest }, prevBenefits = []) => {
    const {
      productSummary: { prodSummary = { uRL: "" } },
      lastApproved: lastSelectedPlan,
      benefitID,
      type,
      selectable,
    } = rest;

    // Always get the first item
    const productSummary = { url: [].concat(prodSummary)[0].uRL };

    // Retrieve benefit configuration
    const benefitConfig = data
      .filter(
        obj =>
          obj.id === "BenefitName" ||
          obj.id === "BenefitDescription" ||
          obj.id === "BriefDescription" ||
          obj.id === "Logo"
      )
      .reduce((obj, item) => {
        if (item.id === "BenefitName") {
          obj.benefitName = item.value;
        } else if (item.id === "BenefitDescription") {
          obj.benefitTooltip = item.value;
        } else if (item.id === "BriefDescription") {
          obj.benefitDescription = item.value;
        } else if (item.id === "Logo") {
          obj.logo = item.value;
        }

        return obj;
      }, {});

    const benefitSavedData = data.filter(
      obj =>
        obj.id === "BenefitOptions" ||
        obj.id === "DefaultOption" ||
        obj.id === "RecommendedOption" ||
        obj.id === "SelectedOption"
    );

    // Find dependencies:
    const { dataListing: { record = [] } = {} } = benefitsRest || {};
    const allBenefits = Array.isArray(record) ? record : [].concat(record);
    const parentBenefit = getParentDependency(data);
    const childBenefits = getChildDependencies(data, allBenefits);
    const isBenefitSelectable = selectable === "true";

    const loadSavePlans = loadBenefitSaveData(
      lastSelectedPlan,
      benefitSavedData,
      benefitID,
      benefitsData,
      benefitsRest,
      uiSelection
    );

    riskmedicalPerMemberUtils.applyBenefitChildrenMemberValidation({
      benefitID,
      allBenefits,
      parentBenefit,
      childBenefits,
      loadSavePlans,
      prevBenefits,
    });

    return {
      benefitId: benefitID,
      benefitUrl: productSummary.url,
      ...benefitConfig,
      benefitSumAssuredPrice:
        type === "SumAssuredBased" ? lastSelectedPlan.name : lastSelectedPlan.description,
      isSumAssured: type === "SumAssuredBased",
      lastSelectedPlan: {
        planId: lastSelectedPlan.id,
        description: lastSelectedPlan.description,
        name: lastSelectedPlan.name,
        members: lastSelectedPlan.members,
      },
      parent: parentBenefit,
      children: childBenefits,
      updateBenefits,
      isUpdated: false,
      selectable: isBenefitSelectable,
      ...loadSavePlans,
    };
  };

  const prevBenefits = [];

  return record.reduce(
    (obj, item) => [...obj, transformBenefitData(item, prevBenefits)],
    []
  );
};

export const getBenefitsSelectedTotalAmount = benefitsData => {
  let total = 0;

  benefitsData.forEach(benefit => {
    if (benefit.filters) {
      let isDependentSelected;

      benefit.plans.forEach(plan => {
        isDependentSelected = plan.options.find(
          option => option.isSelected && option.type !== "EE"
        );

        plan.options.forEach(option => {
          //if one dependent selected, exclude EE from calculation
          if (isDependentSelected) {
            if (option.isSelected && option.type !== "EE") {
              total += parseFloat(option.price);
            }
          } else if (option.isSelected) {
            // otherwise add ee
            total += parseFloat(option.price);
          }
        });
      });
    } else {
      benefit.plans.forEach(plan => {
        if (!plan.options && plan.isSelected) {
          const price = parseFloat(plan.price);
          total += plan.isSelected ? price : 0;
        }
      });
    }
  });

  return total;
};

const loadDefaultOptions = benefits => {
  const newBenefits = [...benefits];

  // First we find and select default plan (and default option in each plan) for each benefit
  newBenefits.forEach(benefit => {
    resetBenefitSelection(benefit);

    if (Array.isArray(benefit.plans)) {
      benefit.plans.forEach(plan => {
        plan.isSelected = plan.isDefault === true;

        // Update current group to filter plans correctly in UI
        if (plan.isSelected) {
          benefit.currentGroupName = plan.groupName;
        }

        if (benefit.selectionMode === "individual") {
          plan.eligibleMembers = riskmedicalPerMemberUtils.applyDefaultMemberSelection(
            plan,
            benefit
          );
        }

        // Update options selection state for all plans
        if (plan.options && Array.isArray(plan.options)) {
          plan.options.forEach(
            option => (option.isSelected = plan.isSelected && plan.defaultPlan === option.planId)
          );
          if (benefit.selectionMode === "individual") {
            const selectedMembers = plan.eligibleMembers.members.filter(
              m => (m.isSelected || m.type === "Employee") && m.price
            );
            const addEmployeePrice = plan.planType ? plan.planType === "Group" : false;
            let price = 0;
            selectedMembers.forEach(memberObj => {
              if (plan.isRelativePrice) {
                price += parseFloat(memberObj.price.numericValue);
              } else if (addEmployeePrice) {
                price += parseFloat(memberObj.price.numericValue);
              } else if (!addEmployeePrice && memberObj.type !== "Employee") {
                price += parseFloat(memberObj.price.numericValue);
              }
            });
            plan.price = price;
          }
        }

        // Update intervalOptions selection state
        if (plan.intervalOptions && Array.isArray(plan.intervalOptions)) {
          const defaultPlan = plan.intervalOptions.find(intervalOption => intervalOption.isDefault);

          if (defaultPlan) {
            const { optionId, price, sumAssured } = defaultPlan;

            // Reset plan (interval-based) values to default
            plan.planId = optionId;
            plan.groupValue = optionId;
            plan.planValue = sumAssured;
            plan.sumAssured = sumAssured;
            plan.isSelected = optionId === plan.planId;

            if (benefit.selectionMode === "individual") {
              const selectedMembers = plan.eligibleMembers.members.filter(m => m.isSelected);
              const selectedIntervalOption = plan.intervalOptions
                ? plan.intervalOptions.find(option => option.optionId === plan.planId)
                : null;
              let price = 0;
              if (selectedIntervalOption) {
                const memberPrice = selectedIntervalOption.memberPrice.filter(mp =>
                  selectedMembers.find(member => member.key === mp.memberKey)
                );
                memberPrice.forEach(mp => {
                  price += parseFloat(mp.price.numericValue);
                });
              }
              plan.price = price;
            } else {
              plan.price = price;
            }
          }
        }
      });
    }
  });

  // Apply dependancy options rules to disable some options and plans
  applyDependancyOptions(newBenefits);

  // Update benefits once again to select only available options in each plan (if any)
  newBenefits.forEach(benefit => {
    if (Array.isArray(benefit.plans)) {
      benefit.plans.forEach(plan => {
        // Update for not selected plans only
        if (!plan.isSelected && plan.options && Array.isArray(plan.options)) {
          // Find first available option. Usually EE or EO
          const availableOption = plan.options.find(option => option.isAvailable);

          if (availableOption) {
            // Set first available option as selected for UI
            plan.options.forEach(
              option =>
                (option.isSelected =
                  option.type === availableOption.type && option.planId === availableOption.planId)
            );
          }
        }
      });

      // Find default plan
      const defaultPlanSelection = benefit.plans.find(plan => plan.isDefault && plan.isSelected);

      autoSelectOtherPlanOption(defaultPlanSelection, benefit.plans);
    }
  });

  return newBenefits;
};

export const getPlanDependencyOptions = benefit => {
  return Object.keys(benefit).reduce((result, key) => {
    result[key] = {};
    result[key].plans = {};
    result[key].plans = benefit[key].benefitOptions.option
      ? benefit[key].benefitOptions.option
      : benefit[key].benefitOptions.options;

    return result;
  }, {});
};

export const tryToSelectPlan = (benefits, benefit) => {
  const filters = benefit.filters;
  const filtersIncludeNone = benefit.filtersIncludeNone;
  const groupNameFilter =
    (filters && filters.length > 0) || (filtersIncludeNone && filtersIncludeNone.length > 0)
      ? benefit.currentGroupName
      : null;
  const availablePlans = benefit.plans.filter(plan => checkIfPlanIsAvailable(plan));
  const filteredPlans = groupNameFilter
    ? availablePlans.filter(plan => plan.groupName === groupNameFilter)
    : availablePlans;

  // If there is only one plan we need to select it
  if (filteredPlans && filteredPlans.length === 1) {
    // To prevent recursive loop:
    benefits.forEach(benefit => (benefit.isUpdated = false));

    return updateBenefits(benefits, benefit, filteredPlans[0]);
  }

  return benefits;
};

const initialState = {
  response: null,
  summary: {},
  benefitsMap: {},
  selectedPlan: {},
  enrolmentData: {},
  benefits: [],
  selectionToSave: [],
  defaultSelection: false,
  fetching: false,
  fetched: false,
  saving: false,
  saved: false,
  isCheckout: false,
  isDirty: false,
  error: null,
};

export const transformBenefitsPayload = payload => {
  const data = toCamelImproved(payload);
  const benefits = makeBenefitKeysMap(data);

  // Trasnformation of benefit data
  const { enrolmentDetails, enrolmentDeductionFrom, wellnessPurchaseFrom } = payload;
  const enrolmentData = enrolmentDetails.panel.data.reduce(
    (obj, item) => ({ ...obj, [camelize(item.id)]: item }),
    {}
  );
  const accountData = enrolmentDetails.panel.data.find(element => element.id === "Accounts");
  const purchaseData = enrolmentDetails.panel.data.find(
    element => element.id === "AllowOverpuchase"
  );

  accountData.enrolmentDeductionFrom = enrolmentDeductionFrom;
  accountData.wellnessPurchaseFrom = wellnessPurchaseFrom;
  accountData.allowOverpurchase = purchaseData.enable === "true";
  accountData.overpurchaseLimit = parseFloat(purchaseData.limit);

  // TODO: Remove when done with refactor
  const transformBenefitData = ({ data, ...rest }) =>
    data.reduce((obj, item) => ({ ...obj, [camelize(item.id)]: item }), rest);

  const {
    benefits: {
      dataListing: { ...dataListingRest },
      ...benefitsRest
    },
    ...enrolmentRest
  } = enrolmentData;
  let {
    benefits: {
      dataListing: { record },
    },
  } = enrolmentData;

  if (!Array.isArray(record)) {
    record = [record];
  }

  const benefitsTranform = record.reduce(
    (obj, item) => ({ ...obj, [item.benefitID]: transformBenefitData(item) }),
    {}
  );

  const benefitsData = transformBenefits(
    enrolmentData,
    benefitsTranform,
    data.initEnrolmentUiSelection
  );
  applyDependancyOptions(benefitsData);
  applyPerMemberDependencyOptions(benefitsData);

  accountData.benefitsSelectedTotalAmount = getBenefitsSelectedTotalAmount(benefitsData);

  return {
    response: data,
    summary: accountData,
    benefitsMap: benefits,
    enrolmentData: {
      ...enrolmentRest,
      benefits: { ...benefitsRest, dataListing: { ...dataListingRest } },
    },
    benefits: benefitsData,
    selectionToSave: prepareDataToSave(benefitsData),
    benefitsTranform,
  };
};

export default (state = initialState, action) => {
  switch (action.type) {
    case riskmedicalLoadBegin:
      return { ...state, ...initialState, fetching: true };
    case riskmedicalSaveBegin:
      return { ...state, saving: true, saved: false };
    case riskmedicalLoadFailed:
      return { ...state, fetching: false, error: action.payload };
    case riskmedicalSaveFailed:
      return { ...state, saving: false, saved: false, error: action.payload };
    case riskmedicalLoadSuccess: {
      return {
        ...state,
        fetching: false,
        fetched: true,
        ...action.payload,
      };
    }
    case riskmedicalSaveSuccess:
      return {
        ...state,
        saving: false,
        saved: true,
        response: action.payload.response,
        isCheckout: action.payload.isCheckout,
      };
    case riskmedicalSelectDefaultLastAcceptedCover: {
      const newBenefits = loadDefaultOptions(state.benefits);

      return {
        ...state,
        benefits: newBenefits,
        selectionToSave: prepareDataToSave(newBenefits),
      };
    }
    case riskmedicalBenefitPlanFilter: {
      let newBenefits = Object.assign([], state.benefits);
      const benefit = newBenefits.find(benefit => benefit.benefitId === action.payload.benefit);

      benefit.currentGroupName = action.payload.filter.planGroupName;
      benefit.showOptions = action.payload.filter.filterName !== "No Coverage";

      newBenefits = tryToSelectPlan(newBenefits, benefit);

      return {
        ...state,
        benefits: newBenefits,
        selectionToSave: prepareDataToSave(newBenefits),
      };
    }
    case riskmedicalBenefitPlan: {
      const benefits = state.benefits;
      const { plan, benefit } = action.payload;

      // To prevent recursive loop:
      benefits.forEach(benefit => (benefit.isUpdated = false));

      const newBenefits = updateBenefits(benefits, benefit, plan);

      return {
        ...state,
        benefits: newBenefits,
        selectionToSave: prepareDataToSave(newBenefits),
        selectedPlan: plan,
        isDirty: true,
      };
    }
    case riskmedicalEnrolmentDone: {
      return {
        ...state,
        saving: false,
        saved: false,
        fetched: false,
        fetching: false,
      };
    }
    case riskmedicalCleanState: {
      return Object.assign({}, state, initialState);
    }
    case riskmedicalPerMemberSelection: {
      const benefits = state.benefits;
      const { benefit, plan } = action.payload;

      // To prevent recursive loop:
      benefits.forEach(benefit => (benefit.isUpdated = false));

      const newBenefits = updateBenefits(benefits, benefit, plan);

      return {
        ...state,
        selectionToSave: prepareDataToSave(newBenefits),
        selectedPlan: plan,
        isDirty: true,
        benefits: newBenefits,
      };
    }
    default:
      return state; // return previous state
  }
};



// WEBPACK FOOTER //
// ./reducers/riskmedicalReducer.js